import React, { useEffect, useRef, useState } from 'react';
import { useMap } from 'react-leaflet';

/**
 * Molette de la souris: ne zoome la carte que si elle a le focus.
 *
 * Une carte posée au milieu d'une page qui capte la molette bloque le
 * défilement: on se retrouve à zoomer au lieu de descendre. Ici la carte
 * (montée avec `scrollWheelZoom={false}`) ne réagit à la molette qu'après un
 * clic dedans, ou avec Ctrl (Cmd sur Mac) + molette. Elle redevient neutre dès
 * que le pointeur la quitte. Un court message, qui disparaît seul, dit quoi
 * faire quand l'utilisateur tourne la molette sans l'avoir activée.
 */
export default function MoletteSurFocus({ delaiMessageMs = 1600 }) {
  const carte = useMap();
  const [message, setMessage] = useState(false);
  const minuteur = useRef(null);
  const dernierZoom = useRef(0);

  useEffect(() => {
    const conteneur = carte.getContainer();

    const activer = () => carte.scrollWheelZoom.enable();
    const desactiver = () => carte.scrollWheelZoom.disable();

    const surMolette = (evenement) => {
      if (carte.scrollWheelZoom.enabled()) return;
      if (evenement.ctrlKey || evenement.metaKey) {
        // Ctrl + molette: zoom de la carte, jamais celui de la page.
        evenement.preventDefault();
        const maintenant = Date.now();
        if (maintenant - dernierZoom.current < 250) return;
        dernierZoom.current = maintenant;
        if (evenement.deltaY < 0) carte.zoomIn();
        else if (evenement.deltaY > 0) carte.zoomOut();
        return;
      }
      setMessage(true);
      window.clearTimeout(minuteur.current);
      minuteur.current = window.setTimeout(() => setMessage(false), delaiMessageMs);
    };

    desactiver();
    conteneur.addEventListener('click', activer);
    conteneur.addEventListener('mouseleave', desactiver);
    conteneur.addEventListener('wheel', surMolette, { passive: false });
    return () => {
      conteneur.removeEventListener('click', activer);
      conteneur.removeEventListener('mouseleave', desactiver);
      conteneur.removeEventListener('wheel', surMolette);
      window.clearTimeout(minuteur.current);
    };
  }, [carte, delaiMessageMs]);

  if (!message) return null;
  return (
    <div
      role="status"
      className="pointer-events-none absolute inset-x-0 top-1/2 z-[1000] flex -translate-y-1/2 justify-center px-4"
    >
      <span className="rounded-full border border-border bg-card/95 px-3 py-1 text-xs font-medium text-foreground shadow-sm">
        Cliquez sur la carte ou utilisez Ctrl + molette pour zoomer
      </span>
    </div>
  );
}
